import { useState } from "react";

const VitalsDisplay = ({
  isMobile = false,
  vitalsData = [],
  isDarkTheme = true,
}) => {
  // Process data for all vital types
  const processedVitalsData = {};

  // Get all unique vital types
  const vitalTypes = [...new Set(vitalsData?.map((item) => item.type))];

  // Process each vital type
  vitalTypes.forEach((vitalType) => {
    const vitalsOfType = vitalsData?.filter((item) => item.type === vitalType);

    processedVitalsData[vitalType] = vitalsOfType?.map((item) => {
      const date = new Date(item.effectiveDateTime);
      const dateStr = date.toLocaleDateString();
      const timeStr = date.toLocaleTimeString();

      const result = {
        date: dateStr,
        time: timeStr,
        components: {},
        // Use item.status directly instead of calculating it
        status: item.status || "Normal", // Default to "Normal" if status is not provided
      };

      // Process each component in the vital
      item.components.forEach((component) => {
        // Store the component value and unit
        result.components[component.name] = {
          value: component.value,
          unit: component.unit,
        };
      });

      return result;
    });
  });

  // State to track which vital type is displayed
  const [activeTab, setActiveTab] = useState(vitalTypes[0]);

  // Helper to get column configuration based on vital type
  const getColumnsConfig = (vitalType) => {
    if (
      !processedVitalsData[vitalType] ||
      processedVitalsData[vitalType].length === 0
    ) {
      return [];
    }

    // Get all unique component names across all readings of this vital type
    const componentNames = new Set();
    processedVitalsData[vitalType].forEach((reading) => {
      Object.keys(reading.components).forEach((name) => {
        componentNames.add(name);
      });
    });

    // Start with the standard columns
    const columns = [{ id: "date", label: "Date & Time", width: "1.5fr" }];

    // Add columns for each component
    componentNames.forEach((name) => {
      columns.push({
        id: name,
        label: name.replace(" blood pressure", ""), // Shorten the label
        width: "1fr",
      });
    });

    // Add the status column
    columns.push({ id: "status", label: "Status", width: "1fr" });

    return columns;
  };

  // Helper to get column widths as a string for grid template
  const getColumnWidths = (vitalType) => {
    return getColumnsConfig(vitalType)
      .map((col) => col.width)
      .join(" ");
  };

  // Helper to get a display name for vital types
  const getVitalDisplayName = (vitalType) => {
    switch (vitalType) {
      case "BP":
        return "Blood Pressure";
      case "Temp":
        return "Temperature";
      default:
        return vitalType;
    }
  };

  return (
    <div style={styles.container(isDarkTheme)}>
      {/* Tab navigation - dynamically created from available vital types */}
      <div style={styles.tabNavigation}>
        {vitalTypes.map((vitalType) => (
          <button
            key={vitalType}
            style={styles.tabButton(activeTab, vitalType, isDarkTheme)}
            onClick={() => setActiveTab(vitalType)}
          >
            {getVitalDisplayName(vitalType)}
          </button>
        ))}
      </div>

      {/* Dynamic Table for Active Vital Type */}
      {processedVitalsData[activeTab] && (
        <div style={styles.tableContainer(isDarkTheme, isMobile)}>
          {/* Table header */}
          <div
            style={styles.tableHeader(getColumnWidths(activeTab), isDarkTheme)}
          >
            {getColumnsConfig(activeTab).map((column) => (
              <div
                key={column.id}
                style={styles.headerCell(isMobile, isDarkTheme)}
              >
                {column.label}
              </div>
            ))}
          </div>

          {/* Table rows */}
          {processedVitalsData[activeTab].map((reading, index) => (
            <div
              key={index}
              style={styles.tableRow(
                getColumnWidths(activeTab),
                isDarkTheme,
                index === processedVitalsData[activeTab].length - 1
              )}
            >
              {/* Date & Time cell */}
              <div style={styles.tableCell(isMobile, isDarkTheme)}>
                {reading.date}
                <br />
                {reading.time}
              </div>

              {/* Component cells - dynamically render based on available components */}
              {getColumnsConfig(activeTab)
                .filter((col) => col.id !== "date" && col.id !== "status")
                .map((col) => {
                  const component = reading.components[col.id];
                  return (
                    <div
                      key={col.id}
                      style={styles.tableCell(isMobile, isDarkTheme)}
                    >
                      {component ? `${component.value} ${component.unit}` : "-"}
                    </div>
                  );
                })}

              {/* Status cell */}
              <div style={styles.tableCell(isMobile, isDarkTheme)}>
                {reading.status}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Style objects
const styles = {
  // Container styles
  container: (isDarkTheme) => ({
    width: "100%",
    fontFamily: "Arial, sans-serif",
    color: isDarkTheme ? "white" : "#333",
    backgroundColor: isDarkTheme ? "#383465" : "#e8e9ff",
    padding: "12px 0px",
    borderRadius: "8px",
  }),

  // Tab navigation styles
  tabNavigation: {
    display: "flex",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    marginBottom: "0.5rem",
    flexWrap: "wrap",
  },

  tabButton: (activeTab, vitalType, isDarkTheme) => ({
    marginRight: "1rem",
    marginBottom: "0.5rem",
    paddingBottom: "0.25rem",
    fontSize: "0.875rem",
    fontWeight: "500",
    background: "none",
    border: "none",
    borderBottom:
      activeTab === vitalType
        ? isDarkTheme
          ? "2px solid #8A8AFF"
          : "2px solid #5D5FEF"
        : "none",
    color:
      activeTab === vitalType
        ? isDarkTheme
          ? "#8A8AFF"
          : "#5D5FEF"
        : isDarkTheme
        ? "#B8B8D9"
        : "#6c6e8a",
    cursor: "pointer",
  }),

  // Table styles
  tableContainer: (isDarkTheme, isMobile) => ({
    backgroundColor: isDarkTheme ? "#272652" : "#f0f1ff",
    borderRadius: "16px",
    margin: isMobile ? "0 0.5rem 0.5rem" : "0 1rem 1rem",
    overflow: "hidden",
  }),

  tableHeader: (columnWidths, isDarkTheme) => ({
    display: "grid",
    gridTemplateColumns: columnWidths,
    padding: "1rem",
    borderBottom: isDarkTheme
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.1)",
  }),

  headerCell: (isMobile, isDarkTheme) => ({
    fontSize: isMobile ? "0.75rem" : "0.875rem",
    fontWeight: "500",
    color: isDarkTheme ? "white" : "#333",
  }),

  tableRow: (columnWidths, isDarkTheme, isLastRow) => ({
    display: "grid",
    gridTemplateColumns: columnWidths,
    padding: "1rem",
    borderBottom: !isLastRow
      ? isDarkTheme
        ? "1px solid rgba(255, 255, 255, 0.05)"
        : "1px solid rgba(0, 0, 0, 0.05)"
      : "none",
  }),

  tableCell: (isMobile, isDarkTheme) => ({
    fontSize: isMobile ? "0.75rem" : "0.875rem",
    color: isDarkTheme ? "white" : "#333",
  }),
};

export default VitalsDisplay;
